import { useMemo } from 'react'

import type { DailyUsage } from '@/services/apiUsage'

const X_LABEL_STEP = 5

export interface ChartBar {
  key: string
  read: number
  write: number
  readPercent: number
  writePercent: number
  tooltip: string
}

export interface ChartTick {
  value: number
  /** Offset from the bottom of the plot, in % */
  bottom: number
}

export interface ChartXLabel {
  index: number
  label: string
}

/**
 * Rounds up to a "nice" axis max: the smallest of 2, 4, 10, 20, 40, 100, …
 * that is ≥ value. Every candidate is divisible by 2, so the mid tick
 * (max/2) stays an integer.
 */
const NICE_MANTISSAS = [1, 2, 4, 10]

const niceCeil = (value: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const mantissa = NICE_MANTISSAS.find((m) => m >= normalized) ?? 10

  return mantissa * magnitude
}

const dayLabel = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

export const useDailyUsageChart = (daily: DailyUsage[]) =>
  useMemo(() => {
    const maxTotal = Math.max(...daily.map((d) => d.read + d.write), 0)
    const scaleMax = niceCeil(Math.max(maxTotal, 4))

    const bars: ChartBar[] = daily.map((d) => ({
      key: d.date,
      read: d.read,
      write: d.write,
      readPercent: (d.read / scaleMax) * 100,
      writePercent: (d.write / scaleMax) * 100,
      tooltip: `${dayLabel(d.date)} — reads: ${d.read}, writes: ${d.write}`,
    }))

    const ticks: ChartTick[] = [0, scaleMax / 2, scaleMax].map((value) => ({
      value,
      bottom: (value / scaleMax) * 100,
    }))

    const xLabels: ChartXLabel[] = daily
      .map((d, index) => ({ index, label: dayLabel(d.date) }))
      .filter(({ index }) => index % X_LABEL_STEP === 0)

    return { bars, ticks, xLabels }
  }, [daily])
